(() => {
  const initializedSections = new WeakMap();
  const footerMedia = window.matchMedia('(max-width: 640px)');

  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const initializeHeader = (root) => {
    if (!root || initializedSections.has(root)) return;

    const controller = new AbortController();
    const signal = controller.signal;
    const mobileMenu = root.querySelector('.mobile-menu');
    const menuToggle = root.querySelector('[data-action="toggle-menu"][aria-controls]');
    const searchResults = root.querySelector('.search-bar__results');
    const searchResultsInner = root.querySelector('.search-bar__results-inner');
    const searchStatus = root.querySelector('[data-lynktr-search-status]');
    let returnFocusTarget = null;

    const focusableItems = () => mobileMenu
      ? [...mobileMenu.querySelectorAll(focusableSelector)].filter((element) => element.offsetParent !== null)
      : [];

    const handleFocusTrap = (event) => {
      if (event.key !== 'Tab' || !mobileMenu || mobileMenu.getAttribute('aria-hidden') !== 'false') return;
      const items = focusableItems();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const handleEscape = (event) => {
      if (event.key !== 'Escape' || !mobileMenu || mobileMenu.getAttribute('aria-hidden') !== 'false') return;
      menuToggle?.click();
    };

    let menuObserver;
    if (mobileMenu) {
      menuObserver = new MutationObserver(() => {
        const isOpen = mobileMenu.getAttribute('aria-hidden') === 'false';
        if (isOpen) {
          returnFocusTarget = menuToggle;
          requestAnimationFrame(() => focusableItems()[0]?.focus());
        } else if (returnFocusTarget) {
          returnFocusTarget.focus();
          returnFocusTarget = null;
        }
      });
      menuObserver.observe(mobileMenu, { attributes: true, attributeFilter: ['aria-hidden'] });
      document.addEventListener('keydown', handleFocusTrap, { signal });
      document.addEventListener('keydown', handleEscape, { signal });
    }

    let searchObserver;
    if (searchResults && searchResultsInner && searchStatus) {
      searchObserver = new MutationObserver(() => {
        const isBusy = searchResults.classList.contains('is-loading');
        searchResults.setAttribute('aria-busy', String(isBusy));
        if (isBusy) {
          searchStatus.textContent = 'Loading search suggestions.';
          return;
        }
        const resultLinks = searchResultsInner.querySelectorAll('a[href]');
        searchStatus.textContent = resultLinks.length
          ? `${resultLinks.length} search suggestions available.`
          : 'No suggestions found. Press Enter to search all results.';
      });
      searchObserver.observe(searchResultsInner, { childList: true, subtree: true });
      searchObserver.observe(searchResults, { attributes: true, attributeFilter: ['class'] });
    }

    initializedSections.set(root, {
      destroy: () => {
        controller.abort();
        menuObserver?.disconnect();
        searchObserver?.disconnect();
        initializedSections.delete(root);
      }
    });
  };

  const syncFooter = (footer) => {
    const details = footer.querySelectorAll('.lynktr-footer__menus details');
    details.forEach((item) => {
      if (footerMedia.matches) {
        if (!item.dataset.lynktrTouched) item.open = false;
      } else {
        item.open = true;
      }
    });
  };

  const initializeFooter = (root) => {
    if (!root || initializedSections.has(root)) return;
    const controller = new AbortController();
    const signal = controller.signal;
    root.querySelectorAll('.lynktr-footer__menus details').forEach((item) => {
      item.addEventListener('toggle', () => {
        if (footerMedia.matches) item.dataset.lynktrTouched = 'true';
      }, { signal });
    });
    const handleMedia = () => syncFooter(root);
    footerMedia.addEventListener('change', handleMedia, { signal });
    syncFooter(root);
    initializedSections.set(root, {
      destroy: () => {
        controller.abort();
        initializedSections.delete(root);
      }
    });
  };

  const initializeAll = (scope = document) => {
    scope.querySelectorAll('[data-section-type="header"]').forEach(initializeHeader);
    scope.querySelectorAll('[data-section-type="footer"]').forEach(initializeFooter);
  };

  if (!window.__lynktrInteractionDispatcher) {
    window.__lynktrInteractionDispatcher = true;
    document.addEventListener('click', (event) => {
      const target = event.target.closest('[data-ga-event]');
      if (!target) return;
      document.dispatchEvent(new CustomEvent('lynktr:interaction', {
        detail: {
          event_name: target.dataset.gaEvent,
          interaction_location: target.dataset.interactionLocation,
          destination_group: target.dataset.destinationGroup,
          link_role: target.dataset.linkRole
        }
      }));
    });
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const sectionRoot = event.target.querySelector('[data-section-type]');
    initializedSections.get(sectionRoot)?.destroy();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }
})();
